import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  UserIcon,
  MapPinIcon,
  BriefcaseIcon,
  FileSignatureIcon,
  UserCogIcon,
} from 'lucide-react'
import ActivateUserAler from './activiate-user-alert'
import { memo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser } from '@/features/auth/state/auth'
import { ROUTE_BUILDERS } from '@/config/routes'
import { useDeleteUser } from '../services/useDeleteUser'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/lib/hooks/use-toast'
import { AxiosError } from 'axios'
import { useUpdateUserComissao } from '../services/useUpdateUserComissao'
import { useUpdateUserCargo } from '../services/useUpdateUserCargo'
import { numeroPorExtenso } from '@/utils/numeroPorExtenso'

import { DEFAULT_USER_PARTNERSHIP_CONTRACT } from '@/types/contrato-servico'
import { useCreateAgreementUser } from '@/features/agreement/services/useCreateAgreementUser'
import { User } from '@/types/user'
import {
  InfoUsuarioAccountingSection,
  InfoUsuarioAddressSection,
  InfoUsuarioHeader,
  InfoUsuarioPersonalSection,
  InfoUsuarioProfessionalSection,
  InfoUsuarioRelationshipsSection,
  InfoUsuarioSignatureSection,
} from './info-usuario.sections'
import { getInfoUsuarioViewModel } from './info-usuario.utils'

type InfoUsuarioProps = {
  user: User
  handleDeleteUser: () => void
}

export default memo(function InfoUsuario({ user, handleDeleteUser }: InfoUsuarioProps) {
  const currentUser = useCurrentUser()
  const { toast } = useToast()
  const navigate = useNavigate()
  const mutation = useDeleteUser()
  const queryClient = useQueryClient()
  const [isEditingCargo, setIsEditingCargo] = useState(false)
  const [isEditingComissao, setIsEditingComissao] = useState(false)

  const [cargoForm, setCargoForm] = useState<string>(user.typeCargo ?? '')
  const [comissaoForm, setComissaoForm] = useState<string>(user.comissao_user != null ? String(user.comissao_user) : '')
  const {
    displayName,
    initials,
    createdDateDisplay,
    createdByDisplay,
    responsibleDisplay,
    isCNPJ,
  } = getInfoUsuarioViewModel(user)

  const handleClick = () => {
    if (user.id) {
      navigate(ROUTE_BUILDERS.minuteUser(user.id))
    }
  }

  const handleClickDeleteUser = () => {
    if (!user.id) return
    mutation.mutate(user.id, {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ['gerente']
        })
        queryClient.refetchQueries({
          queryKey: ['user', 'tenant']
        })
        handleDeleteUser()
      },
      onError: error => {
        console.error('Erro ao excluir usuário:', error)
        const message = error instanceof AxiosError ? error.response?.data.message : 'Erro ao tentar deletar usuário.'
        toast({
          title: 'Erro',
          description: message,
          variant: 'destructive'
        })
      }
    })
  }
  const { mutate: createAgreement } = useCreateAgreementUser()
  const handleEnviarMinuta = async (email: string) => {
    createAgreement({ contracts: [DEFAULT_USER_PARTNERSHIP_CONTRACT], email })
  }
  // no topo do componente (depois dos useState atuais)
  const { mutate: updateUserCargo } = useUpdateUserCargo({
    onSuccess: () => setIsEditingCargo(false)
  })

  const { mutate: updateUserComissao } = useUpdateUserComissao({
    onSuccess: () => {
      setIsEditingComissao(false)
      handleEnviarMinuta(user.email || '')
    }
  })

  const handleSaveCargo = () => {
    if (!user.id) return
    if (!cargoForm) {
      toast({ title: 'Atenção', description: 'Selecione um cargo.', variant: 'destructive' })
      return
    }

    updateUserCargo({ id: user.id, typeCargo: cargoForm })
  }

  const handleCancelCargo = () => {
    setCargoForm(user.typeCargo ?? '')
    setIsEditingCargo(false)
  }
  // salvar comissão
  const handleSaveComissao = () => {
    if (!user.id) return
    const valorNumerico = parseInt(comissaoForm, 10)

    if (Number.isNaN(valorNumerico)) {
      toast({ title: 'Atenção', description: 'Informe um número válido.', variant: 'destructive' })
      return
    }
    if (valorNumerico < 0 || valorNumerico > 100) {
      toast({ title: 'Atenção', description: 'A comissão deve estar entre 0 e 100.', variant: 'destructive' })
      return
    }

    const valorExtenso = numeroPorExtenso(valorNumerico)

    updateUserComissao({
      id: user.id,
      comissao_user: valorNumerico,
      comissao_user_escrita: valorExtenso
    })
  }

  // cancelar comissão
  const handleCancelComissao = () => {
    setComissaoForm(user.comissao_user != null ? String(user.comissao_user) : '')
    setIsEditingComissao(false)
  }

  const canDelete = currentUser?.id !== user.id

  return (
    <div className="w-full space-y-6">
      <InfoUsuarioHeader
        user={user}
        initials={initials}
        displayName={displayName}
        canDelete={Boolean(currentUser?.typeCargo === 'CEO' && canDelete)}
        isDeleting={mutation.isPending}
        onViewMinute={handleClick}
        onDelete={handleClickDeleteUser}
      />

      <ActivateUserAler user={user} />

      <Tabs defaultValue="personal" className="w-full">
        <TabsList className={`grid h-auto w-full p-1 ${isCNPJ ? 'grid-cols-3 lg:grid-cols-5' : 'grid-cols-3 lg:grid-cols-4'}`}>
          <TabsTrigger value="personal" className="gap-2 py-3">
            <UserIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Pessoal</span>
          </TabsTrigger>
          <TabsTrigger value="address" className="gap-2 py-3">
            <MapPinIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Endereço</span>
          </TabsTrigger>
          <TabsTrigger value="signature" className="gap-2 py-3">
            <FileSignatureIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Assinatura</span>
          </TabsTrigger>
          <TabsTrigger value="relationships" className="gap-2 py-3">
            <UserCogIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Vínculos</span>
          </TabsTrigger>
          {isCNPJ && (
            <TabsTrigger value="accounting" className="gap-2 py-3">
              <BriefcaseIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Empresa</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="personal" className="mt-6 space-y-6">
          <InfoUsuarioPersonalSection
            user={user}
            displayName={displayName}
            createdDateDisplay={createdDateDisplay}
          />

          {currentUser?.typeCargo === `CEO` && (
            <InfoUsuarioProfessionalSection
              user={user}
              isEditingCargo={isEditingCargo}
              isEditingComissao={isEditingComissao}
              cargoForm={cargoForm}
              comissaoForm={comissaoForm}
              onEditCargo={() => setIsEditingCargo(true)}
              onSaveCargo={handleSaveCargo}
              onCancelCargo={handleCancelCargo}
              onCargoChange={setCargoForm}
              onEditComissao={() => setIsEditingComissao(true)}
              onSaveComissao={handleSaveComissao}
              onCancelComissao={handleCancelComissao}
              onComissaoChange={setComissaoForm}
            />
          )}
        </TabsContent>

        <TabsContent value="relationships" className="mt-6">
          <InfoUsuarioRelationshipsSection
            createdByDisplay={createdByDisplay}
            responsibleDisplay={responsibleDisplay}
          />
        </TabsContent>

        <TabsContent value="address" className="mt-6">
          <InfoUsuarioAddressSection user={user} />
        </TabsContent>

        {isCNPJ && (
          <TabsContent value="accounting" className="mt-6">
            <InfoUsuarioAccountingSection user={user} />
          </TabsContent>
        )}

        <TabsContent value="signature" className="mt-6">
          <InfoUsuarioSignatureSection user={user} />
        </TabsContent>
      </Tabs>
    </div>
  )
})
